import { ConfirmDialog } from "@/components/confirm-dialog";
import { useAppForm } from "@/components/form/form-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UnsavedChangesGuard } from "@/hooks/unsaved-changes-guard";
import { deleteOrganisationFn, organisationQueryOptions, updateOrganisationFn } from "@/server/organisations";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { hasUpdatePermission, hasDeletePermission, ContextUser } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/rename")({
  component: RouteComponent,
  loader: async ({ params, context }) => {
    return {
      crumb: "Rename",
      orgId: params.orgId,
      user: context.user as ContextUser | null | undefined,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { orgId, user } = Route.useLoaderData();
  const organisation = useQuery(organisationQueryOptions(Number(orgId))).data;
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const updateOrganisationMutation = useMutation({
    mutationFn: updateOrganisationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      queryClient.invalidateQueries({ queryKey: ["organisation", Number(orgId)] });
      navigate({ to: "/organisations" });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const deleteOrganisationMutation = useMutation({
    mutationFn: deleteOrganisationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      navigate({ to: "/organisations" });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const initiateFormValues = () => {
    return {
      id: organisation?.id || "",
      name: organisation?.name || "",
    };
  };

  const form = useAppForm({
    defaultValues: initiateFormValues(),
    onSubmit: async (formData) => {
      updateOrganisation(formData.value);
    },
  });

  const updateOrganisation = async (payload: any) => {
    try {
      const organisationPromise = updateOrganisationMutation.mutateAsync({ data: { orgId: Number(orgId), payload } });
      toast.promise(organisationPromise, {
        loading: "Updating organisation...",
        success: (data) => {
          return `Organisation updated: ${data?.organisation.name}`;
        },
        error: (error) => error.message || "Failed to update organisation",
      });
    } catch (error) {
      console.error(error);
    }
  };

  const deleteOrganisation = async (organisation: any) => {
    setSelectedOrganisation(organisation);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    confirmOrganisationDeletion(selectedOrganisation?.id || "");
  };

  const confirmOrganisationDeletion = async (id: string) => {
    try {
      const deleteOrganisationPromise = deleteOrganisationMutation.mutateAsync({ data: { orgId: Number(id) } });
      toast.promise(deleteOrganisationPromise, {
        loading: "Deleting organisation...",
        success: () => {
          return "Organisation deleted";
        },
        error: (error) => error.message || "Failed to delete organisation",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setDialogOpen(false);
      setLoading(false);
    }
  };

  const getError = (field: any) => {
    const error = form.getAllErrors().fields;
    const errorName = Object.keys(error).find((key) => key === field.name);
    if (errorName) {
      const errorMessage = (error as Record<string, any>)[errorName].errors[0][errorName];
      return errorMessage;
    }
    return undefined;
  };

  return (
    <>
      <form.Subscribe
        selector={(state: any) => state.isDirty}
        children={(isDirty: boolean) => <UnsavedChangesGuard isDirty={isDirty} />}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Rename organisation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <form.AppField
              name="id"
              children={(field) => (
                <field.FormTextField label="ID" id="id" description="ID of the organisation" readOnly />
              )}
            />
            <form.AppField
              name="name"
              validators={{
                onChange: (value) => {
                  if (value.value.length < 1) {
                    return {
                      name: "Name is required",
                    };
                  }
                },
              }}
              children={(field) => (
                <field.FormTextField
                  label="Name"
                  id="name"
                  description="Name of the organisation"
                  error={getError(field)}
                />
              )}
            />
          </CardContent>
        </Card>
        <form.Subscribe selector={(s) => [s.isSubmitting, s.isDirty, s.canSubmit]}>
          {([isSubmitting, isDirty, canSubmit]) => {
            const maySave = hasUpdatePermission(user, "ORGANISATION");
            const mayDelete = hasDeletePermission(user, "ORGANISATION");
            return (
              <div className="flex justify-between gap-2 sticky bottom-0 border-t p-4 bg-background z-10">
                <div className="flex gap-2">
                  {maySave && (
                    <Button type="submit" disabled={isSubmitting || !isDirty || !canSubmit}>
                      {isSubmitting ? "Saving..." : "Save"}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSubmitting || !isDirty || !canSubmit}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      form.reset(initiateFormValues());
                    }}
                  >
                    Discard
                  </Button>
                </div>
                {mayDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => deleteOrganisation(organisation)}
                    disabled={isSubmitting}
                  >
                    Delete
                  </Button>
                )}
              </div>
            );
          }}
        </form.Subscribe>
      </form>
      <ConfirmDialog
        open={dialogOpen}
        title="Delete organisation"
        description={
          selectedOrganisation
            ? `Are you sure you want to delete the organisation "${selectedOrganisation.name}"?
           All Users, Settings, Divergence reports, and other related information will be removed.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedOrganisation(null);
        }}
        loading={loading}
      />
    </>
  );
}
